import { assertCanHandle } from '@lucidlink/tscore';
import { AuxDataKey } from '#block-store/types';
import { GetRequestStatsContext } from '#block-store/utils';
import { Logging } from '#common/logging';
import { withStatsSync } from '#common/stats';
import { CompressionError } from '#common/types';
import { canHandleAllErrors } from '#common/util';
import { CompressorStats } from './types/stats';
export function createCompressorStore(blockStore, wasmLz4, wasmUtils) {
    return new CompressorStore(blockStore, wasmLz4, wasmUtils);
}
class CompressorStore {
    blockStore;
    compressor;
    utils;
    log = Logging.getLogger('CompressorStore');
    compressorStats = new CompressorStats(this.log);
    decoder = new TextDecoder();
    encoder = new TextEncoder();
    constructor(blockStore, compressor, utils) {
        this.blockStore = blockStore;
        this.compressor = compressor;
        this.utils = utils;
    }
    async get(request) {
        const context = new GetRequestStatsContext(request, this.compressorStats.get);
        try {
            const blocksPromises = await this.blockStore.get(request);
            return blocksPromises.map((blockPromise) => this.decompressBlock(blockPromise, context));
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            context.onRequestFailed();
            throw err;
        }
    }
    async put(request) {
        request.blocks = withStatsSync(this.compressorStats.put, () => {
            return request.blocks.map((block) => this.compressBlock(block));
        }, { id: request.inodeId, size: request.blocks.length });
        return await this.blockStore.put(request);
    }
    stats() {
        return this.compressorStats;
    }
    async stop() {
        await this.blockStore.stop();
    }
    doDecompressBlock(block) {
        return withStatsSync(this.compressorStats.decompress, () => {
            const ctx = this.createDecompressCtx(block);
            if (!this.isCompressed(ctx)) {
                return block;
            }
            const decompressedData = this.decompress(ctx);
            this.compressorStats.decompress.compressedBytesRead.update(ctx.compressedData.length);
            this.compressorStats.decompress.uncompressedBytesRead.update(decompressedData.length);
            return this.createDecompressedBlock(block, decompressedData);
        }, { size: block.data.dataBuffer.length });
    }
    async decompressBlock(blockPromise, context) {
        try {
            const block = await blockPromise;
            const decompressed = this.doDecompressBlock(block);
            context.onGetSucceeded();
            return decompressed;
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            context.onGetFailed();
            throw err;
        }
    }
    createDecompressCtx(block) {
        const auxData = this.utils.deserializeAuxData(block.data.auxBuffer);
        const ctx = {
            compressedData: block.data.dataBuffer,
        };
        const compressionSchemeEntry = auxData.get(AuxDataKey.DATA_COMPRESSION_SCHEME);
        if (compressionSchemeEntry) {
            ctx.compressionScheme = this.decoder.decode(compressionSchemeEntry);
        }
        const decompressedSizeEntry = auxData.get(AuxDataKey.DATA_UNCOMPRESSED_SIZE);
        if (decompressedSizeEntry) {
            ctx.decompressedSize = Number(this.decoder.decode(decompressedSizeEntry));
        }
        return ctx;
    }
    isCompressed(ctx) {
        return ctx.compressionScheme && ctx.decompressedSize && ctx.decompressedSize !== ctx.compressedData.length;
    }
    assertIsCompressed(ctx) {
        if (!this.isCompressed(ctx)) {
            throw new CompressionError('Expected compressed block, received uncompressed');
        }
    }
    decompress(ctx) {
        this.assertIsCompressed(ctx);
        if (ctx.decompressedSize < ctx.compressedData.length) {
            throw new CompressionError(`Decompress failed: uncompressed size ${ctx.decompressedSize} is smaller than the compressed size ${ctx.compressedData.length}.`);
        }
        let decompressedData;
        try {
            decompressedData = this.compressor.decompress(ctx.compressedData, ctx.decompressedSize);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof CompressionError);
            throw new CompressionError(`Decompress failed: ${err.message}`, err.devDetails);
        }
        if (decompressedData.length != ctx.decompressedSize) {
            throw new CompressionError(`Decompress failed: uncompressed size mismatch. Expected ${ctx.decompressedSize} but uncompressed data size is ${decompressedData.length}`);
        }
        return decompressedData;
    }
    createDecompressedBlock(compressedBlock, decompressedData) {
        return {
            blockId: compressedBlock.blockId,
            data: {
                dataBuffer: decompressedData,
                auxBuffer: compressedBlock.data.auxBuffer,
            },
        };
    }
    compressBlock(block) {
        const uncompressedData = block.data.dataBuffer;
        if (uncompressedData.length === 0) {
            return block;
        }
        const compressedData = this.tryCompress(uncompressedData);
        if (uncompressedData.length < compressedData.length) {
            throw new CompressionError('Compress failed: Uncompressed size is smaller than the compressed size');
        }
        return this.createCompressedBlock(block, compressedData);
    }
    tryCompress(uncompressedData) {
        try {
            return withStatsSync(this.compressorStats.compress, () => {
                const compressedData = this.compressor.compress(uncompressedData);
                if (uncompressedData.length !== 0 && compressedData.length === 0) {
                    throw new CompressionError('Compress failed: Compression library error');
                }
                return compressedData;
            }, { size: uncompressedData.length });
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof CompressionError);
            return uncompressedData;
        }
    }
    createCompressedBlock(uncompressedBlock, compressedData) {
        const auxData = this.utils.deserializeAuxData(uncompressedBlock.data.auxBuffer);
        const uncompressedData = uncompressedBlock.data.dataBuffer;
        const isCompressed = compressedData.length !== uncompressedData.length;
        if (isCompressed) {
            auxData.set(AuxDataKey.DATA_COMPRESSION_SCHEME, this.encoder.encode(this.compressor.getName()));
            auxData.set(AuxDataKey.DATA_UNCOMPRESSED_SIZE, this.encoder.encode(uncompressedData.length.toString()));
            this.compressorStats.compress.compressedBytesWritten.update(compressedData.length);
            this.compressorStats.compress.uncompressedBytesWritten.update(uncompressedData.length);
        }
        else {
            auxData.delete(AuxDataKey.DATA_COMPRESSION_SCHEME);
            auxData.delete(AuxDataKey.DATA_UNCOMPRESSED_SIZE);
        }
        const auxSerialized = this.utils.serializeAuxData(auxData, compressedData.length);
        return {
            blockId: uncompressedBlock.blockId,
            data: {
                dataBuffer: compressedData,
                auxBuffer: auxSerialized,
            },
        };
    }
}
